import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, formatApiErrorDetail } from '../lib/api';
import { Btn, Card, Input, Label, PageHeader } from '../components/Ui';
import { Folder, FileText, Download, ChevronRight, ArrowUp, RefreshCw, Home, Trash2 } from 'lucide-react';

function fmtSize(n) {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export default function WasabiBrowser() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get('prefix') || '';
  const [prefix, setPrefix] = useState(initial);
  const [pendingPrefix, setPendingPrefix] = useState(initial);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deletingKey, setDeletingKey] = useState(null);

  const load = useCallback(async (p) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/api/wasabi/list', { params: { prefix: p } });
      setData(data);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(prefix);
    const next = new URLSearchParams(searchParams);
    if (prefix) next.set('prefix', prefix);
    else next.delete('prefix');
    setSearchParams(next, { replace: true });
    setPendingPrefix(prefix);
  }, [prefix, load]); // eslint-disable-line

  const open = (newPrefix) => setPrefix(newPrefix);

  const download = async (key) => {
    try {
      const { data } = await api.get('/api/wasabi/signed-url', { params: { key } });
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      alert(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const deleteObject = async (file) => {
    const ok = window.confirm(
      `¿Eliminar DEFINITIVAMENTE el objeto Wasabi?\n\n${file.key}\n\nNo se puede recuperar.`
    );
    if (!ok) return;
    setDeletingKey(file.key);
    try {
      await api.delete('/api/wasabi/object', { params: { key: file.key } });
      await load(prefix);
    } catch (e) {
      alert(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setDeletingKey(null);
    }
  };

  // Breadcrumb segments
  const segments = prefix
    ? prefix.replace(/\/$/, '').split('/').map((seg, i, arr) => ({
        label: seg,
        prefix: arr.slice(0, i + 1).join('/') + '/',
      }))
    : [];

  return (
    <>
      <PageHeader title="Wasabi Browser" subtitle={data?.bucket ? `Bucket: ${data.bucket}` : ''} testid="wasabi-header">
        <Btn variant="secondary" onClick={() => load(prefix)} data-testid="wasabi-refresh">
          <RefreshCw size={14} /> Actualizar
        </Btn>
      </PageHeader>

      <div className="p-8">
        {/* Breadcrumb / path */}
        <Card testid="wasabi-breadcrumb" className="px-4 py-3 mb-4 flex items-center gap-2 flex-wrap text-xs">
          <button
            data-testid="wasabi-root"
            onClick={() => open('')}
            className={`inline-flex items-center gap-1 px-2 py-1 hover:bg-surface ${!prefix ? 'font-bold' : ''}`}
          >
            <Home size={12} /> /
          </button>
          {segments.map((s) => (
            <React.Fragment key={s.prefix}>
              <ChevronRight size={12} className="text-muted" />
              <button
                onClick={() => open(s.prefix)}
                data-testid={`crumb-${s.label}`}
                className="px-2 py-1 hover:bg-surface font-mono"
              >
                {s.label}
              </button>
            </React.Fragment>
          ))}
        </Card>

        {/* Manual prefix input */}
        <Card className="mb-4 p-4">
          <Label>Prefijo manual</Label>
          <div className="flex gap-2">
            <Input
              data-testid="wasabi-prefix-input"
              value={pendingPrefix}
              onChange={(e) => setPendingPrefix(e.target.value)}
              placeholder="facturas/BPW/2026/05/"
              onKeyDown={(e) => {
                if (e.key === 'Enter') setPrefix(pendingPrefix);
              }}
            />
            <Btn data-testid="wasabi-prefix-go" onClick={() => setPrefix(pendingPrefix)}>
              Ir
            </Btn>
            {data?.parent !== null && data?.parent !== undefined && (
              <Btn
                variant="secondary"
                onClick={() => open(data.parent)}
                data-testid="wasabi-up"
              >
                <ArrowUp size={14} /> Subir
              </Btn>
            )}
          </div>
        </Card>

        {error && (
          <Card testid="wasabi-error" className="p-4 mb-4 border-danger">
            <div className="text-danger text-xs">{error}</div>
          </Card>
        )}

        {/* Listing */}
        <Card testid="wasabi-listing">
          <div className="border-b border-ink/90 px-4 py-3 flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-wide2">
              {prefix || '/'}{' '}
              {data && (
                <span className="text-muted font-normal">
                  — {data.folders.length} carpetas, {data.files.length} archivos
                </span>
              )}
            </h2>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-surface text-muted">
              <tr className="text-[10px] uppercase tracking-wide2">
                <th className="px-3 py-2 text-left font-bold w-8"></th>
                <th className="px-3 py-2 text-left font-bold">Nombre</th>
                <th className="px-3 py-2 text-right font-bold">Tamaño</th>
                <th className="px-3 py-2 text-left font-bold">Última modificación</th>
                <th className="px-3 py-2 text-right font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-muted">Cargando…</td></tr>
              )}
              {!loading && data && data.folders.length === 0 && data.files.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-muted">Carpeta vacía</td></tr>
              )}
              {!loading && data && data.folders.map((f) => {
                const name = f.replace(prefix, '').replace(/\/$/, '');
                return (
                  <tr
                    key={f}
                    data-testid={`folder-${name}`}
                    onClick={() => open(f)}
                    className="border-t border-border hover:bg-surface cursor-pointer"
                  >
                    <td className="px-3 py-2"><Folder size={14} className="text-accent" /></td>
                    <td className="px-3 py-2 font-mono">{name}/</td>
                    <td className="px-3 py-2 text-right num text-muted">—</td>
                    <td className="px-3 py-2 text-muted">—</td>
                    <td className="px-3 py-2 text-right text-muted">abrir</td>
                  </tr>
                );
              })}
              {!loading && data && data.files.map((f) => (
                <tr
                  key={f.key}
                  data-testid={`file-${f.name}`}
                  className="border-t border-border hover:bg-surface"
                >
                  <td className="px-3 py-2"><FileText size={14} className="text-muted" /></td>
                  <td className="px-3 py-2 font-mono break-all">{f.name}</td>
                  <td className="px-3 py-2 text-right num">{fmtSize(f.size)}</td>
                  <td className="px-3 py-2 num">{fmtDate(f.last_modified)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap space-x-1">
                    <Btn
                      data-testid={`download-${f.name}`}
                      size="sm"
                      variant="secondary"
                      onClick={() => download(f.key)}
                    >
                      <Download size={12} /> Abrir
                    </Btn>
                    <Btn
                      data-testid={`delete-${f.name}`}
                      size="sm"
                      variant="danger"
                      disabled={deletingKey === f.key}
                      onClick={() => deleteObject(f)}
                    >
                      <Trash2 size={12} />
                      {deletingKey === f.key ? '…' : ''}
                    </Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </>
  );
}
